import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import {
  User, Package, FileText, Settings, LogOut, ChevronRight,
  Download, Eye, Clock, CheckCircle, Truck, XCircle, Loader2,
  Edit, Save, X, Mail, Phone, MapPin, Calendar, Shield, AlertCircle,
  Plus, Trash2, Home, Building, Receipt, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import Price from '@/components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;

// Status badge colors and icons
const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-800 hover:text-white', icon: Clock, label: 'Pending' },
  confirmed: { color: 'bg-blue-100 text-blue-800 hover:text-white', icon: CheckCircle, label: 'Confirmed' },
  processing: { color: 'bg-purple-100 text-purple-800 hover:text-white', icon: Package, label: 'Processing' },
  shipped: { color: 'bg-indigo-100 text-indigo-800 hover:text-white', icon: Truck, label: 'Shipped' },
  delivered: { color: 'bg-green-100 text-green-800 hover:text-white', icon: CheckCircle, label: 'Delivered' },
  cancelled: { color: 'bg-red-100 text-red-800 hover:text-white', icon: XCircle, label: 'Cancelled' }
};

// Profile Section Component
const ProfileSection = ({ user, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
  });
  
  // Phone verification state
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        email: user.email || '',
      });
    }
  }, [user]);
  
  // OTP cooldown timer
  useEffect(() => {
    if (otpCooldown > 0) {
      const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCooldown]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    }
    setLoading(false);
  };
  
  const handleSendOtp = async () => {
    setSendingOtp(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/send-phone-otp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OTP sent to your phone');
      setShowOtpInput(true);
      setOtpCooldown(30);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send OTP');
    }
    setSendingOtp(false);
  };
  
  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }
    setVerifyingOtp(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/verify-phone-otp`, { otp: otpCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Phone number verified successfully!');
      setShowOtpInput(false);
      setOtpCode('');
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid OTP');
    }
    setVerifyingOtp(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-xl">Profile Information</CardTitle>
          <CardDescription>Manage your personal details</CardDescription>
        </div>
        {!editing ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Edit className="h-4 w-4 mr-2" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setEditing(false); setFormData({ name: user?.name || '', phone: user?.phone || '', email: user?.email || '' }); }}>
              <X className="h-4 w-4 mr-2" /> Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={loading} className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save</>}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-[#0c1b44]/10 flex items-center justify-center">
            <User className="h-10 w-10 text-[#0c1b44]" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-navy">{user?.name}</h3>
            <p className="text-muted-foreground">{user?.email}</p>
            <Badge className="mt-1 bg-[#0c1b44]/10 text-[#0c1b44] border-0">
              {user?.role === 'admin' ? 'Administrator' : 'Customer'}
            </Badge>
          </div>
        </div>

        <Separator />

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" /> Full Name
            </Label>
            {editing ? (
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            ) : (
              <p className="text-navy font-medium">{user?.name || '-'}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" /> Email Address
            </Label>
            {editing ? (
              <Input
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                type="email"
              />
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-navy font-medium">{user?.email}</p>
                {user?.email_verified ? (
                  <Badge className="bg-green-100 text-green-700 border-0">
                    <CheckCircle className="h-3 w-3 mr-1" /> Verified
                  </Badge>
                ) : (
                  <Link to="/verify-account">
                    <Badge className="bg-orange-100 text-orange-700 border-0 cursor-pointer hover:bg-orange-200">
                      <AlertCircle className="h-3 w-3 mr-1" /> Verify Now
                    </Badge>
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" /> Phone Number
            </Label>
            {editing ? (
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+971 50 123 4567"
              />
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-navy font-medium">{user?.phone || 'Not provided'}</p>
                  {user?.phone && (
                    user?.phone_verified ? (
                      <Badge className="bg-green-100 text-green-700 border-0">
                        <CheckCircle className="h-3 w-3 mr-1" /> Verified
                      </Badge>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                        onClick={handleSendOtp}
                        disabled={sendingOtp || otpCooldown > 0}
                      >
                        {sendingOtp ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Shield className="h-3 w-3 mr-1" />}
                        {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Verify Now'}
                      </Button>
                    )
                  )}
                </div>
                
                {/* OTP Input for phone verification */}
                {showOtpInput && !user?.phone_verified && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <Input
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="Enter 4-digit OTP"
                      className="w-32 h-9 text-center text-lg tracking-widest font-mono"
                      maxLength={4}
                    />
                    <Button 
                      size="sm" 
                      onClick={handleVerifyOtp} 
                      disabled={verifyingOtp || otpCode.length < 4}
                      className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white h-9"
                    >
                      {verifyingOtp ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => { setShowOtpInput(false); setOtpCode(''); }}
                      className="h-9"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" /> Member Since
            </Label>
            <p className="text-navy font-medium">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-AE', {
                year: 'numeric', month: 'long', day: 'numeric'
              }) : '-'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Address Section Component
const AddressSection = ({ user, onUpdate }) => {
  const { getAuthHeaders, settings } = useApp();
  const parseAddresses = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
    return [];
  };
  const [addresses, setAddresses] = useState(parseAddresses(user?.addresses));
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    label: 'Home',
    full_name: user?.name || '',
    phone: user?.phone || '',
    address_line1: '',
    address_line2: '',
    city: 'Dubai',
    emirate: 'Dubai',
    is_default: false
  });

  const emirates = settings?.delivery_emirates || ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];

  useEffect(() => {
    setAddresses(parseAddresses(user?.addresses));
  }, [user]);

  const resetForm = () => {
    setFormData({
      label: 'Home',
      full_name: user?.name || '',
      phone: user?.phone || '',
      address_line1: '',
      address_line2: '',
      city: 'Dubai',
      emirate: 'Dubai',
      is_default: addresses.length === 0
    });
    setEditingAddress(null);
  };

  const handleEdit = (address, index) => {
    setFormData({ ...address });
    setEditingAddress(index);
    setShowAddDialog(true);
  };

  const handleDelete = async (index) => {
    if (!confirm('Are you sure you want to delete this address?')) return;

    setLoading(true);
    try {
      const newAddresses = addresses.filter((_, i) => i !== index);
      await axios.put(`${API}/users/addresses`, { addresses: newAddresses }, { headers: getAuthHeaders() });
      setAddresses(newAddresses);
      toast.success('Address deleted');
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error('Failed to delete address');
    }
    setLoading(false);
  };

  const handleSetDefault = async (index) => {
    setLoading(true);
    try {
      const newAddresses = addresses.map((addr, i) => ({
        ...addr,
        is_default: i === index
      }));
      await axios.put(`${API}/users/addresses`, { addresses: newAddresses }, { headers: getAuthHeaders() });
      setAddresses(newAddresses);
      toast.success('Default address updated');
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error('Failed to update default address');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!formData.address_line1 || !formData.city || !formData.emirate || !formData.full_name || !formData.phone) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      let newAddresses;
      if (editingAddress !== null) {
        // Update existing address
        newAddresses = addresses.map((addr, i) => i === editingAddress ? formData : addr);
      } else {
        // Add new address
        const newAddress = { ...formData, id: Date.now().toString() };
        if (newAddress.is_default) {
          newAddresses = addresses.map(a => ({ ...a, is_default: false }));
          newAddresses.push(newAddress);
        } else {
          newAddresses = [...addresses, newAddress];
        }
      }

      await axios.put(`${API}/users/addresses`, { addresses: newAddresses }, { headers: getAuthHeaders() });
      setAddresses(newAddresses);
      toast.success(editingAddress !== null ? 'Address updated' : 'Address added');
      setShowAddDialog(false);
      resetForm();
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error('Failed to save address');
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-xl">Saved Addresses</CardTitle>
          <CardDescription>Manage your delivery addresses</CardDescription>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white">
              <Plus className="h-4 w-4 mr-2" /> Add Address
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingAddress !== null ? 'Edit Address' : 'Add New Address'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Address Label</Label>
                  <Select value={formData.label} onValueChange={(v) => setFormData({ ...formData, label: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Home"><div className="flex items-center gap-2"><Home className="h-4 w-4" /> Home</div></SelectItem>
                      <SelectItem value="Office"><div className="flex items-center gap-2"><Building className="h-4 w-4" /> Office</div></SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="John Doe" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+971 50 123 4567" />
              </div>
              <div className="space-y-2">
                <Label>Address Line 1 *</Label>
                <Input value={formData.address_line1} onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })} placeholder="Building name, street address" />
              </div>
              <div className="space-y-2">
                <Label>Address Line 2 (Optional)</Label>
                <Input value={formData.address_line2 || ''} onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })} placeholder="Apartment, suite, floor, etc." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Dubai" />
                </div>
                <div className="space-y-2">
                  <Label>Emirate *</Label>
                  <Select value={formData.emirate} onValueChange={(v) => setFormData({ ...formData, emirate: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emirates.map((e) => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is_default" className="cursor-pointer">Set as default address</Label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>Cancel</Button>
                <Button onClick={handleSave} disabled={loading} className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save Address</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {addresses.length === 0 ? (
          <div className="text-center py-8">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No saved addresses yet</p>
            <Button variant="outline" onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add Your First Address
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {addresses.map((address, index) => (
              <div key={index} className={`p-4 rounded-lg border-2 ${address.is_default ? 'border-[#0c1b44] bg-[#0c1b44]/5' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${address.is_default ? 'bg-[#0c1b44]/10' : 'bg-gray-100'}`}>
                      {address.label === 'Home' ? <Home className="h-5 w-5 text-[#0c1b44]" /> :
                        address.label === 'Office' ? <Building className="h-5 w-5 text-blue-500" /> :
                          <MapPin className="h-5 w-5 text-gray-500" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-navy">{address.label}</h4>
                        {address.is_default && (
                          <Badge className="bg-[#0c1b44]/10 text-[#0c1b44] border-0 text-xs">Default</Badge>
                        )}
                      </div>
                      <p className="font-medium">{address.full_name}</p>
                      <p className="text-sm text-muted-foreground">{address.address_line1}</p>
                      {address.address_line2 && <p className="text-sm text-muted-foreground">{address.address_line2}</p>}
                      <p className="text-sm text-muted-foreground">{address.city}, {address.emirate}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <Phone className="h-3 w-3" /> {address.phone}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {!address.is_default && (
                      <Button size="sm" variant="ghost" onClick={() => handleSetDefault(index)} title="Set as default">
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(address, index)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Quote Detail Modal — shows prices and accept/pay button
const QuoteDetailModal = ({ order, onAccepted }) => {
  const { getAuthHeaders, API } = useApp();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);

  const loadQuote = async () => {
    try {
      const res = await axios.get(`${API}/orders/${order.id}/quote`, { headers: getAuthHeaders() });
      setQuote(res.data);
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load quote details');
    }
  };

  const handleAccept = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders/${order.id}/accept-quote`, {}, { headers: getAuthHeaders() });
      if (res.data.payment_method === 'stripe' && res.data.checkout_url) {
        toast.success('Redirecting to payment...');
        window.location.href = res.data.checkout_url;
      } else {
        toast.success(res.data.message || 'Quote accepted! Our team will contact you for payment.');
        setOpen(false);
        if (onAccepted) onAccepted();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept quote');
    }
    setLoading(false);
  };

  const AED = ({ value }) => <Price amount={value} showIcon={true} />;

  const q = quote || {};
  const validUntil = q.quote_valid_until ? new Date(q.quote_valid_until).toLocaleDateString('en-AE', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const expired = q.quote_valid_until && new Date(q.quote_valid_until) < new Date();

  return (
    <>
      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold" onClick={loadQuote}>
        <Eye className="h-4 w-4 mr-1" /> View Quote
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0c1b44] to-[#1a3673] text-white px-6 py-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Price Quotation</h2>
                  <p className="text-white/70 text-sm mt-0.5">Order #{q.order_number}</p>
                </div>
                <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Expiry Warning */}
              {expired && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  This quotation has expired. Please contact us for a new quote.
                </div>
              )}
              {!expired && validUntil && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-amber-800 text-sm">
                  <Clock className="h-4 w-4 flex-shrink-0" />
                  Valid until <strong>{validUntil}</strong>
                </div>
              )}

              {/* Items Table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-[#0c1b44] text-white">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Product</th>
                      <th className="text-center px-3 py-3 font-semibold">Qty</th>
                      <th className="text-right px-4 py-3 font-semibold">Unit Price</th>
                      <th className="text-right px-4 py-3 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(q.quoted_items || []).map((item, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 font-medium text-gray-800">{item.product_name}</td>
                        <td className="px-3 py-3 text-center text-gray-600">{item.quantity}</td>
                        <td className="px-4 py-3 text-right text-gray-700"><AED value={item.unit_price} /></td>
                        <td className="px-4 py-3 text-right font-semibold text-[#0c1b44]"><AED value={item.total_price} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600"><span>Subtotal</span><AED value={q.quoted_subtotal} /></div>
                <div className="flex justify-between text-sm text-gray-600"><span>VAT (5%)</span><AED value={q.quoted_vat} /></div>
                <div className="flex justify-between text-sm text-gray-600"><span>Shipping</span>{q.quoted_shipping === 0 ? <span className="text-green-600 font-medium">FREE</span> : <AED value={q.quoted_shipping} />}</div>
                <div className="border-t border-[#d4af37] pt-2 mt-2 flex justify-between items-center">
                  <span className="font-bold text-[#0c1b44] text-lg">Total</span>
                  <span className="font-bold text-[#d4af37] text-xl"><AED value={q.quoted_total} /></span>
                </div>
              </div>

              {/* Admin Notes */}
              {q.quote_admin_notes && (
                <div className="bg-blue-50 border-l-4 border-[#0c1b44] p-4 rounded-r-lg">
                  <p className="text-sm font-semibold text-[#0c1b44] mb-1">Note from our team:</p>
                  <p className="text-sm text-gray-700">{q.quote_admin_notes}</p>
                </div>
              )}

              {/* Accept & Pay Button */}
              {!expired && (
                <Button
                  className="w-full h-12 bg-[#0c1b44] hover:bg-[#1a2d64] text-white font-bold text-base"
                  onClick={handleAccept}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle className="h-5 w-5 mr-2" />Accept & Proceed to Payment</>}
                </Button>
              )}
              <p className="text-center text-xs text-gray-500">By accepting, you agree to the quoted price above. Our team will confirm your order shortly.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Order Card Component
const OrderCard = ({ order, onDownloadInvoice, onRefresh }) => {
  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const [downloading, setDownloading] = useState(false);

  // Quote-aware logic
  const isQuotationOrder = order.payment_method === 'quotation';
  const quoteStatus = order.quote_status; // null | 'quoted' | 'accepted'
  const isConfirmed = order.status === 'confirmed' || order.payment_status === 'paid';
  const showPrice = !isQuotationOrder || isConfirmed || quoteStatus === 'accepted';
  const showInvoiceBtn = isConfirmed;
  const showQuoteReadyBanner = isQuotationOrder && quoteStatus === 'quoted';
  const showAcceptedBanner = isQuotationOrder && quoteStatus === 'accepted' && !isConfirmed;

  const handleDownload = async () => {
    setDownloading(true);
    await onDownloadInvoice(order.id, order.invoice_number);
    setDownloading(false);
  };

  return (
    <Card className="hover:shadow-md transition-shadow overflow-hidden">
      {/* Quote Ready Banner */}
      {showQuoteReadyBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-2">
          <FileText className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-amber-800 text-sm font-medium flex-1">
            📋 Your price quotation is ready! Review and accept to proceed.
          </p>
        </div>
      )}
      {/* Quote Accepted Banner */}
      {showAcceptedBanner && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2.5 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <p className="text-blue-800 text-sm font-medium">✅ Quote accepted — Awaiting payment confirmation from our team.</p>
        </div>
      )}

      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-semibold text-navy">{order.order_number}</h3>
              <Badge className={status.color}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {status.label}
              </Badge>
              {isQuotationOrder && !isConfirmed && (
                <Badge className="bg-gray-100 text-gray-600 hover:text-white">Quotation</Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(order.created_at).toLocaleDateString('en-AE')}
              </span>
              <span className="flex items-center gap-1">
                <Package className="h-4 w-4" />
                {order.items?.length || 0} items
              </span>
              {showPrice ? (
                <span className="font-semibold text-[#0c1b44]">
                  <Price amount={order.total} size="sm" showIcon={true} />
                </span>
              ) : (
                <span className="text-gray-400 italic text-xs">Price upon quotation</span>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Link to={`/order/${order.id}`}>
              <Button variant="outline" size="sm">
                <Eye className="h-4 w-4 mr-2" /> View
              </Button>
            </Link>

            {/* Show "View Quote" button when quote is ready */}
            {showQuoteReadyBanner && (
              <QuoteDetailModal order={order} onAccepted={onRefresh} />
            )}

            {/* Show invoice download only when confirmed/paid */}
            {showInvoiceBtn && (
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading}>
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" />Invoice</>}
              </Button>
            )}
          </div>
        </div>

        {/* Order Items Preview */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {order.items?.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={item.image || "https://via.placeholder.com/60"}
                  alt={item.product_name}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {order.items?.length > 4 && (
              <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-sm text-muted-foreground">
                +{order.items.length - 4}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Orders Section Component
const OrdersSection = () => {
  const { getAuthHeaders } = useApp();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders`, { headers: getAuthHeaders() });
      setOrders(res.data.orders || []);
    } catch (e) {
      console.error('Failed to fetch orders', e);
    }
    setLoading(false);
  };

  const downloadInvoice = async (orderId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/invoice`, { headers: getAuthHeaders() });

      const url = `data:application/pdf;base64,${res.data.content}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename || `invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Invoice downloaded successfully');
    } catch (e) {
      toast.error('Failed to download invoice');
    }
  };

  const filteredOrders = filter === 'all'
    ? orders
    : orders.filter(o => o.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0c1b44]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'confirmed', 'processing', 'shipped', 'delivered'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
            className={filter === status ? 'bg-[#0c1b44] hover:bg-[#1a2d64] text-white' : ''}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && (
              <span className="ml-1 text-xs">
                ({orders.filter(o => o.status === status).length})
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-navy mb-2">No Orders Found</h3>
            <p className="text-muted-foreground mb-4">
              {filter === 'all'
                ? "You haven't placed any orders yet."
                : `No ${filter} orders found.`}
            </p>
            <Link to="/products">
              <Button className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white">
                Start Shopping
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onDownloadInvoice={downloadInvoice}
              onRefresh={fetchOrders}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Customer Invoices Section ──────────────────────────────────────────────
const InvoicesSection = () => {
  const { getAuthHeaders } = useApp();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => { fetchInvoices(); }, []); // eslint-disable-line

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customer/invoices`, { headers: getAuthHeaders() });
      setInvoices(res.data.invoices || []);
    } catch (e) { toast.error('Failed to load invoices'); }
    setLoading(false);
  };

  const handleDownload = async (invoiceId, invoiceNumber) => {
    setDownloading(invoiceId);
    try {
      const res = await axios.get(`${API}/customer/invoices/${invoiceId}/pdf`, { headers: getAuthHeaders() });
      const url = `data:application/pdf;base64,${res.data.content}`;
      const link = document.createElement('a');
      link.href = url; link.download = res.data.filename || `${invoiceNumber}.pdf`;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link);
      toast.success('Invoice downloaded');
    } catch (e) { toast.error('Failed to download invoice'); }
    setDownloading(null);
  };

  const paymentColor = {
    paid: 'bg-green-100 text-green-800 hover:text-white',
    unpaid: 'bg-orange-100 text-orange-800 hover:text-white',
    partial: 'bg-yellow-100 text-yellow-800 hover:text-white',
    overdue: 'bg-red-100 text-red-800 hover:text-white',
    sent: 'bg-blue-100 text-blue-800 hover:text-white',
    draft: 'bg-gray-100 text-gray-800 hover:text-white',
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0c1b44]" /></div>;

  if (invoices.length === 0) return (
    <Card>
      <CardContent className="py-16 text-center">
        <Receipt className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-navy mb-2">No Invoices Yet</h3>
        <p className="text-muted-foreground">Invoices sent by our team will appear here.</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      {invoices.map(inv => (
        <Card key={inv.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Receipt className="h-5 w-5 text-[#0c1b44]" />
                  <h3 className="font-semibold text-navy">{inv.invoice_number}</h3>
                  <Badge className={paymentColor[inv.payment_status] || 'bg-gray-100 text-gray-800'}>
                    {(inv.payment_status || 'unpaid').toUpperCase()}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {inv.invoice_date?.slice(0,10)}
                  </span>
                  {inv.due_date && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" /> Due: {inv.due_date.slice(0,10)}
                    </span>
                  )}
                  <span className="font-semibold text-[#0c1b44]">
                    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="0.9em" height="0.9em" style={{verticalAlign:'middle',position:'relative',top:'-0.05em',marginRight:'2px'}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>
                    {inv.total_amount?.toFixed(2)}
                  </span>
                  {inv.order_number && <span>Order: {inv.order_number}</span>}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownload(inv.id, inv.invoice_number)}
                disabled={downloading === inv.id}
              >
                {downloading === inv.id
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><Download className="h-4 w-4 mr-2" />Download PDF</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Order Detail Page Component (for viewing single order)
export const OrderDetailPage = () => {
  const { id } = useParams();
  const { getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${id}`, { headers: getAuthHeaders() });
      setOrder(res.data);
    } catch (e) {
      toast.error('Order not found');
      navigate('/account');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#0c1b44]" />
      </div>
    );
  }

  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  // Quote-aware logic (same as OrderCard)
  const isQuotationOrder = order.payment_method === 'quotation';
  const quoteStatus = order.quote_status;
  const isConfirmed = order.status === 'confirmed' || order.payment_status === 'paid';
  const showPrices = !isQuotationOrder || isConfirmed || quoteStatus === 'accepted';
  const showInvoice = isConfirmed;
  const showQuoteReady = isQuotationOrder && quoteStatus === 'quoted';
  const showAwaitingQuote = isQuotationOrder && !quoteStatus && !isConfirmed;
  const showAcceptedPending = isQuotationOrder && quoteStatus === 'accepted' && !isConfirmed;

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/orders/${id}/invoice`, { headers: getAuthHeaders() });
      const url = `data:application/pdf;base64,${res.data.content}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename || `invoice_${order.order_number}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      toast.success('Invoice downloaded');
    } catch (e) {
      toast.error('Failed to download invoice');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/account" className="hover:text-[#0c1b44]">My Account</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/account?tab=orders" className="hover:text-[#0c1b44]">Orders</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-navy">{order.order_number}</span>
        </div>

        {/* Quote-status banners at top */}
        {showAwaitingQuote && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Clock className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Awaiting Price Quotation</p>
              <p className="text-amber-700 text-sm mt-0.5">Your request has been received. Our team is preparing your custom price quote. You will receive an email once it is ready.</p>
            </div>
          </div>
        )}
        {showQuoteReady && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
            <FileText className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-green-800">📋 Your Price Quotation is Ready!</p>
              <p className="text-green-700 text-sm mt-0.5">Review the quoted prices below and click "Accept & Pay" to confirm your order.</p>
            </div>
          </div>
        )}
        {showAcceptedPending && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-blue-800">✅ Quote Accepted — Awaiting Payment Confirmation</p>
              <p className="text-blue-700 text-sm mt-0.5">Our team will contact you to arrange payment. Once confirmed, your invoice will be emailed automatically.</p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Order Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">Order {order.order_number}</CardTitle>
                    <CardDescription>
                      Placed on {new Date(order.created_at).toLocaleDateString('en-AE', {
                        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </CardDescription>
                  </div>
                  <Badge className={`${status.color} text-sm`}>
                    <StatusIcon className="h-4 w-4 mr-1" />
                    {status.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {/* Order Status Timeline */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-4">Order Progress</h4>
                  <div className="flex items-center justify-between">
                    {['pending', 'confirmed', 'processing', 'shipped', 'delivered'].map((s, idx) => {
                      const isActive = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'].indexOf(order.status) >= idx;
                      const config = statusConfig[s];
                      const Icon = config.icon;
                      return (
                        <div key={s} className="flex flex-col items-center flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isActive ? 'bg-[#0c1b44] text-white' : 'bg-gray-100 text-gray-400'
                            }`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className={`text-xs mt-2 ${isActive ? 'text-[#0c1b44] font-medium' : 'text-muted-foreground'}`}>
                            {config.label}
                          </span>
                          {idx < 4 && (
                            <div className={`absolute h-0.5 w-full ${isActive ? 'bg-[#0c1b44]' : 'bg-gray-200'}`}
                              style={{ left: '50%', top: '20px', width: 'calc(100% - 40px)', marginLeft: '20px' }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <Separator className="my-6" />

                {/* Order Items */}
                <h4 className="font-semibold mb-4">Order Items</h4>
                <div className="space-y-4">
                  {/* Use quoted_items if quote has been sent, else original items */}
                  {(showQuoteReady ? order.quoted_items : order.items)?.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={item.image || "https://via.placeholder.com/80"}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-navy">{item.product_name}</h5>
                        {item.sku && <p className="text-sm text-muted-foreground">SKU: {item.sku}</p>}
                        <div className="flex justify-between mt-1">
                          <span className="text-sm">Qty: {item.quantity}</span>
                          {showPrices ? (
                            <span className="font-semibold text-[#0c1b44]">
                              <span className="whitespace-nowrap inline-flex items-center">
                                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>
                                {(item.total_price ?? item.unit_price * item.quantity)?.toFixed(2)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-sm">Price on quotation</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Shipping Address */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-[#0c1b44]" />
                  Shipping Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium">{order.shipping_address?.full_name}</p>
                <p className="text-muted-foreground">{order.shipping_address?.address_line1}</p>
                <p className="text-muted-foreground">
                  {order.shipping_address?.city}, {order.shipping_address?.emirate}
                </p>
                <p className="text-muted-foreground">Phone: {order.shipping_address?.phone}</p>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary Sidebar */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">
                  {showQuoteReady ? 'Price Quotation' : 'Order Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {showPrices ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{(order.quoted_subtotal ?? order.subtotal)?.toFixed(2)}</span></span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">VAT (5%)</span>
                      <span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{(order.quoted_vat ?? order.vat_amount)?.toFixed(2)}</span></span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Shipping</span>
                      <span>{(order.quoted_shipping ?? order.shipping_cost) === 0 ? 'FREE' : `AED ${(order.quoted_shipping ?? order.shipping_cost)?.toFixed(2)}`}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-[#0c1b44]"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{(order.quoted_total ?? order.total)?.toFixed(2)}</span></span>
                    </div>
                  </>
                ) : (
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <Clock className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-amber-800">Awaiting Quotation</p>
                    <p className="text-xs text-amber-700 mt-1">Pricing will appear here once our team sends your custom quote.</p>
                  </div>
                )}

                <div className="pt-2 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Payment Method</span>
                    <span className="font-medium capitalize">
                      {order.payment_method === 'cod' ? 'Cash on Delivery' : order.payment_method === 'quotation' ? 'Quotation' : 'Card Payment'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Payment Status</span>
                    <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                      {order.payment_status?.toUpperCase()}
                    </Badge>
                  </div>
                </div>

                {/* Accept & Pay — only when quote is ready */}
                {showQuoteReady && (
                  <QuoteDetailModal order={order} onAccepted={() => fetchOrder()} />
                )}

                {/* Download Invoice — only when confirmed */}
                {showInvoice && (
                  <Button className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white mt-2" onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Invoice
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Account Page Component
const AccountPage = () => {
  const { user, logout, refreshUser } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
    // Check URL params for tab
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) setActiveTab(tab);
  }, [user, navigate]);

  if (!user) return null;

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-[#0c1b44]">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-navy">My Account</span>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-4">
                {/* User Info */}
                <div className="flex items-center gap-3 pb-4 border-b">
                  <div className="w-12 h-12 rounded-full bg-[#0c1b44]/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-[#0c1b44]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy">{user.name}</h3>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>

                {/* Navigation */}
                <nav className="mt-4 space-y-1">
                  <button
                    onClick={() => setActiveTab('profile')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${activeTab === 'profile'
                      ? 'bg-[#0c1b44]/10 text-[#0c1b44] font-medium'
                      : 'text-muted-foreground hover:bg-muted'
                      }`}
                  >
                    <User className="h-5 w-5" />
                    Profile
                  </button>
                  <button
                    onClick={() => setActiveTab('addresses')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${activeTab === 'addresses'
                      ? 'bg-[#0c1b44]/10 text-[#0c1b44] font-medium'
                      : 'text-muted-foreground hover:bg-muted'
                      }`}
                  >
                    <MapPin className="h-5 w-5" />
                    Addresses
                  </button>
                  <button
                    onClick={() => setActiveTab('orders')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${activeTab === 'orders'
                      ? 'bg-[#0c1b44]/10 text-[#0c1b44] font-medium'
                      : 'text-muted-foreground hover:bg-muted'
                      }`}
                  >
                    <Package className="h-5 w-5" />
                    My Orders
                  </button>
                  <button
                    onClick={() => setActiveTab('invoices')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      activeTab === 'invoices'
                        ? 'bg-[#0c1b44]/10 text-[#0c1b44] font-medium'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Receipt className="h-5 w-5" />
                    My Invoices
                  </button>
                  {user.role === 'admin' && (
                    <Link
                      to="/admin"
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-muted-foreground hover:bg-muted transition-colors"
                    >
                      <Settings className="h-5 w-5" />
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-5 w-5" />
                    Sign Out
                  </button>
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && (
              <ProfileSection user={user} onUpdate={refreshUser} />
            )}

            {activeTab === 'addresses' && (
              <AddressSection user={user} onUpdate={refreshUser} />
            )}

            {activeTab === 'orders' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-navy">My Orders</h2>
                    <p className="text-muted-foreground">Track and manage your orders</p>
                  </div>
                </div>
                <OrdersSection />
              </div>
            )}

            {activeTab === 'invoices' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-navy">My Invoices</h2>
                    <p className="text-muted-foreground">View and download invoices sent to you</p>
                  </div>
                </div>
                <InvoicesSection />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
